import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    PendingActionState,
    PendingSummary,
} from 'pbts/c1/api/accessreview/v1/accessreview_pending'

export type PendingActionsStateV2 = {
    counts: PendingSummary | undefined
    isLoading: boolean
    taskToPendingActionState: Record<string, PendingActionState> | undefined
    optimisticTaskToPendingActionState: Record<string, PendingActionState> | undefined
    consolidatedPendingActionState: Record<string, PendingActionState>
    isBulkActionInProgress: boolean
    invalidateAfterTimestamp: number | undefined
}

// TODO Make this access review specific
const initialState: PendingActionsStateV2 = {
    counts: undefined,
    taskToPendingActionState: undefined,
    isLoading: true,
    optimisticTaskToPendingActionState: undefined,
    consolidatedPendingActionState: {},
    isBulkActionInProgress: false,
    invalidateAfterTimestamp: undefined,
}

export const pendingActionsSliceV2 = createSlice({
    name: 'pendingActionsV2',
    initialState,
    reducers: {
        // ===== Counts =====
        setPendingActionCounts(state, action: PayloadAction<PendingSummary>) {
            state.counts = action.payload
        },
        clearPendingActionCounts(state) {
            state.counts = PendingSummary.create({
                totalCount: 0,
                approveCount: 0,
                denyCount: 0,
                conflictCount: 0,
            })
        },

        // ===== Task to Pending Action State (backend) =====
        setTaskToPendingActionStates(state, action: PayloadAction<Record<string, PendingActionState>>) {
            state.taskToPendingActionState = { ...state.taskToPendingActionState, ...action.payload }

            // Remove the optimistic state
            Object.keys(action.payload).forEach((taskId) => {
                if (state.optimisticTaskToPendingActionState) {
                    delete state.optimisticTaskToPendingActionState[taskId]
                }
            })

            // Consolidate the pending action states
            state.consolidatedPendingActionState = { ...state.optimisticTaskToPendingActionState, ...state.taskToPendingActionState }

            state.isLoading = false
        },
        removeTaskToPendingActionState(state, action: PayloadAction<string>) {
            if (!state.taskToPendingActionState) {
                state.taskToPendingActionState = {}
            }
            delete state.taskToPendingActionState[action.payload]

            // Consolidate the pending action states
            state.consolidatedPendingActionState = { ...state.optimisticTaskToPendingActionState, ...state.taskToPendingActionState }
        },
        // ===== Optimistic Task to Pending Action State =====
        setIsLoadingPendingActionsState(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload
        },
        setOptimisticTaskToPendingActionState(state, action: PayloadAction<{ taskId: string; pendingActionState: PendingActionState }>) {
            if (!state.optimisticTaskToPendingActionState) {
                state.optimisticTaskToPendingActionState = {}
            }
            state.optimisticTaskToPendingActionState[action.payload.taskId] = action.payload.pendingActionState

            // Consolidate the pending action states
            state.consolidatedPendingActionState = { ...state.taskToPendingActionState, ...state.optimisticTaskToPendingActionState }
        },
        removeOptimisticTaskToPendingActionState(state, action: PayloadAction<string>) {
            if (state.optimisticTaskToPendingActionState) {
                delete state.optimisticTaskToPendingActionState[action.payload]
            }

            // Consolidate the pending action states
            state.consolidatedPendingActionState = { ...state.taskToPendingActionState, ...state.optimisticTaskToPendingActionState }
        },

        clearAllPendingActionStates(state) {
            state.taskToPendingActionState = {}
            state.optimisticTaskToPendingActionState = {}
            state.consolidatedPendingActionState = {}
        },

        // ===== Bulk Action Progress =====
        setIsBulkActionInProgress(state, action: PayloadAction<boolean>) {
            state.isBulkActionInProgress = action.payload
        },

        // ===== Invalidate After Timestamp =====
        setInvalidateAfterTimestamp(state, action: PayloadAction<number>) {
            state.invalidateAfterTimestamp = action.payload
        },
    },
})

export const {
    setPendingActionCounts,
    clearPendingActionCounts,
    setTaskToPendingActionStates,
    removeTaskToPendingActionState,
    clearAllPendingActionStates,
    setIsLoadingPendingActionsState,
    setOptimisticTaskToPendingActionState,
    removeOptimisticTaskToPendingActionState,
    setIsBulkActionInProgress,
    setInvalidateAfterTimestamp,
} = pendingActionsSliceV2.actions

// Selectors
export const selectPendingActionCounts = (
    state: { pendingActionsV2: PendingActionsStateV2 },
): PendingSummary | undefined => state.pendingActionsV2.counts

export const selectTaskToPendingActionState = (
    state: { pendingActionsV2: PendingActionsStateV2 },
    taskId: string,
): PendingActionState | undefined => state.pendingActionsV2.taskToPendingActionState?.[taskId]

export const selectOptimisticTaskToPendingActionState = (
    state: { pendingActionsV2: PendingActionsStateV2 },
    taskId: string,
): PendingActionState | undefined => state.pendingActionsV2.optimisticTaskToPendingActionState?.[taskId]

export const selectIsLoadingPendingActionStates = (
    state: { pendingActionsV2: PendingActionsStateV2 },
): boolean => state.pendingActionsV2.isLoading

export const selectAllTaskToPendingActionStates = (
    state: { pendingActionsV2: PendingActionsStateV2 },
): Record<string, PendingActionState> | undefined => state.pendingActionsV2.taskToPendingActionState

export const selectConsolidatedPendingActionState = (
    state: { pendingActionsV2: PendingActionsStateV2 },
    taskId: string,
): PendingActionState | undefined => state.pendingActionsV2.consolidatedPendingActionState?.[taskId]

export const selectIsBulkActionInProgress = (
    state: { pendingActionsV2: PendingActionsStateV2 },
): boolean => state.pendingActionsV2.isBulkActionInProgress

export const selectInvalidateAfterTimestamp = (
    state: { pendingActionsV2: PendingActionsStateV2 },
): number | undefined => state.pendingActionsV2.invalidateAfterTimestamp
